import { AriaLive } from "../../../services/aria.live.service";
import { S25Const } from "../../../util/s25-const";
import { IPaginatedData, PaginatedLoadResult } from "./paginated.list";
import type { SESearchType } from "./se.search.component";

export namespace PaginatedLoadAnnouncer {
    /** Fast loads finish before this, so announcing "Loading..." would only precede the result by a moment. */
    export const PENDING_ANNOUNCEMENT_DELAY_MS = 400;

    export type LoadKind = "more" | "all";

    /** What the loaded results are called in announcements: an SE search type, or explicit lowercase nouns. */
    export type ResultNoun = SESearchType | { singular: string; plural: string };

    /** The parts of a paginated list the announcer reads; any list with Load More can supply them. */
    export type AnnouncedList = Pick<
        IPaginatedData,
        "items" | "totalCount" | "hasMore" | "loading" | "loadingAll" | "loadMore" | "loadAll"
    >;

    export function searchResultNoun(searchType: SESearchType, count: number): string {
        const display = S25Const.itemId2Display[S25Const.itemName2IdPlural[searchType]];
        return (count === 1 ? display.singular : display.plural).toLowerCase();
    }

    function resultNoun(noun: ResultNoun, count: number): string {
        if (typeof noun === "string") return searchResultNoun(noun, count);
        return count === 1 ? noun.singular : noun.plural;
    }

    /**
     * Runs a Load More / Load All and announces it: a polite pending message if the load is slow,
     * then a polite completion message, or an assertive failure message.
     */
    export async function loadWithAnnouncements(
        list: AnnouncedList,
        loadKind: LoadKind,
        noun: ResultNoun,
        pageSize: number,
    ): Promise<PaginatedLoadResult> {
        const load = () => (loadKind === "all" ? list.loadAll() : list.loadMore());
        if (list.loading() || list.loadingAll() || !list.hasMore()) return load();

        const pendingMessage = buildPendingMessage(list, loadKind, noun, pageSize);
        const pendingTimer = setTimeout(() => void AriaLive.announce(pendingMessage), PENDING_ANNOUNCEMENT_DELAY_MS);

        let result: PaginatedLoadResult;
        try {
            result = await load();
        } finally {
            clearTimeout(pendingTimer);
        }

        if (result.error) {
            void AriaLive.announce(buildFailureMessage(list, noun), true);
        } else {
            void AriaLive.announce(buildCompletionMessage(list, noun));
        }
        return result;
    }

    /** Wraps a paginated list so Load More / Load All triggered by a child component are announced. */
    export function withLoadAnnouncements<T>(
        list: IPaginatedData<T>,
        searchType: () => SESearchType,
        pageSize: number,
    ): IPaginatedData<T> {
        return {
            items: list.items,
            itemsForDisplay: list.itemsForDisplay,
            page: list.page,
            loading: list.loading,
            loadingAll: list.loadingAll,
            totalCount: list.totalCount,
            hasMore: list.hasMore,
            loadMore: () => loadWithAnnouncements(list, "more", searchType(), pageSize),
            loadAll: () => loadWithAnnouncements(list, "all", searchType(), pageSize),
        };
    }

    function buildPendingMessage(list: AnnouncedList, loadKind: LoadKind, noun: ResultNoun, pageSize: number): string {
        const remaining = list.totalCount() - list.items().length;
        if (loadKind === "all") return `Loading all ${remaining} remaining ${resultNoun(noun, remaining)}`;

        const pending = Math.min(pageSize, remaining);
        return `Loading ${pending} more ${resultNoun(noun, pending)}`;
    }

    function buildCompletionMessage(list: AnnouncedList, noun: ResultNoun): string {
        const total = list.totalCount();
        const totalNoun = resultNoun(noun, total);
        if (!list.hasMore()) return `All ${total} ${totalNoun} loaded`;
        return `Showing ${list.items().length} of ${total} ${totalNoun}`;
    }

    function buildFailureMessage(list: AnnouncedList, noun: ResultNoun): string {
        const total = list.totalCount();
        return (
            `Could not load more ${resultNoun(noun, 2)}. ` +
            `Still showing ${list.items().length} of ${total} ${resultNoun(noun, total)}.`
        );
    }
}
